import React from 'react';
import EditableField from "../components/EditableField";
import { sortSkills, categorizeSkills, ResumeLink } from './templateHelpers';

/**
 * TechTemplate — Silicon Valley developer style layout.
 * Left-aligned, sans-serif, technical skills at the top, optimized for tech recruiters.
 */
function TechTemplate({ resumeData, accentColor = '#0891b2', editMode = false, onFieldUpdate = () => {}, onDeleteBullet = null, onDeleteSkill = null }) {
    if (!resumeData) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20 px-8 text-center font-sans">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-5">
                    <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                </div>
                <h3 className="text-slate-500 font-semibold text-sm mb-1.5">No resume data</h3>
                <p className="text-slate-400 text-xs leading-relaxed max-w-[220px]">Enter your details in chat to generate your resume.</p>
            </div>
        );
    }

    const {
        name, email, phone, city, role, summary,
        skills, skillCategories, experience, education, projects,
        certifications, languages, links, achievements, extracurricular,
    } = resumeData;

    const projectList   = Array.isArray(projects)       ? projects       : [];
    const educationList = Array.isArray(education)      ? education      : (education ? [education] : []);
    const languageList  = Array.isArray(languages)      ? languages      : [];
    const certList      = Array.isArray(certifications)  ? certifications  : [];
    const achieveList   = Array.isArray(achievements)    ? achievements    : [];
    const extraList     = Array.isArray(extracurricular) ? extracurricular : [];
    const summaryText   = (summary && summary.trim())    || "";

    // Contact line
    const contactItems = [];
    if (city)  contactItems.push(city);
    if (email) contactItems.push(email);
    if (phone) contactItems.push(phone);

    const stripUrl = (url) => url ? url.replace(/^https?:\/\/(www\.)?/, '') : '';
    const linkItems = [];
    if (links?.linkedin)  linkItems.push({ href: links.linkedin,  label: stripUrl(links.linkedin),  key: 'linkedin' });
    if (links?.github)    linkItems.push({ href: links.github,    label: stripUrl(links.github),    key: 'github' });
    if (links?.portfolio) linkItems.push({ href: links.portfolio, label: stripUrl(links.portfolio), key: 'portfolio' });

    const SectionHeader = ({ title }) => (
        <div className="flex items-center gap-2 mt-4 mb-2 font-sans">
            <div className="w-[3px] h-[13px] rounded-sm shrink-0" style={{ backgroundColor: accentColor }} />
            <h2 className="text-[10px] font-extrabold text-gray-900 uppercase tracking-wider font-sans">
                {title}
            </h2>
            <div className="flex-1 h-[1px] bg-gray-150" />
        </div>
    );

    const EditHint = () => editMode ? (
        <div className="absolute -top-5 right-0 text-[9px] text-emerald-500 font-medium animate-pulse pointer-events-none">
            ✏️ Click any text to edit
        </div>
    ) : null;

    return (
        <div id="resume" className={`bg-white font-sans text-[10.5px] leading-[1.5] text-gray-800 px-8 py-6 relative ${editMode ? 'ring-2 ring-emerald-400/40 ring-inset' : ''}`}>
            {editMode && <EditHint />}

            {/* ─── Left-Aligned Header ─── */}
            <div className="mb-3 font-sans">
                <EditableField
                    editMode={editMode}
                    value={name || ""}
                    onSave={(v) => onFieldUpdate("name", v)}
                    tag="h1"
                    className="text-[23px] font-extrabold text-gray-900 leading-tight tracking-tight inline-block font-sans"
                    placeholder="Your Name"
                />
                {(role || editMode) && (
                    <div className="mt-0.5">
                        <EditableField
                            editMode={editMode}
                            value={role || ""}
                            onSave={(v) => onFieldUpdate("role", v)}
                            tag="p"
                            className="text-[11px] font-bold uppercase tracking-wider inline-block font-sans"
                            style={{ color: accentColor }}
                            placeholder="Your Engineering Role"
                        />
                    </div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[9.5px] text-gray-500 mt-2 font-sans items-center">
                    {contactItems.map((item, i) => (
                        <span key={i} className="flex items-center shrink-0">
                            {item}
                            {i < contactItems.length - 1 && <span className="ml-3 text-gray-300 font-normal">|</span>}
                        </span>
                    ))}
                    {linkItems.length > 0 && contactItems.length > 0 && <span className="text-gray-300 font-normal shrink-0">|</span>}
                    {linkItems.map((item, i) => (
                        <span key={item.key} className="flex items-center shrink-0">
                            {editMode ? (
                                <EditableField
                                    editMode={editMode}
                                    value={item.label}
                                    onSave={(v) => onFieldUpdate(`links.${item.key}`, v)}
                                    tag="span"
                                    style={{ color: accentColor }}
                                />
                            ) : (
                                <ResumeLink
                                    href={item.href}
                                    label={item.label}
                                    style={{ color: accentColor, textDecoration: 'none' }}
                                    className="hover:underline"
                                />
                            )}
                            {i < linkItems.length - 1 && <span className="ml-3 text-gray-300 font-normal">|</span>}
                        </span>
                    ))}
                </div>
            </div>

            {/* ─── Body Sections (Skills first by default for Silicon Valley standards) ─── */}
            {(() => {
                const internships = Array.isArray(resumeData.internships) ? resumeData.internships : [];

                const SECTION_RENDERERS = {
                    skills: () => (skills && skills.length > 0) ? (() => {
                        // Prefer user-defined categories from the form editor;
                        // fall back to auto-categorisation from the flat skills array.
                        let categories;
                        if (Array.isArray(skillCategories) && skillCategories.length > 0) {
                            categories = skillCategories
                                .filter(c => Array.isArray(c.skills) && c.skills.length > 0)
                                .map(c => ({ label: c.name, items: c.skills }));
                        } else {
                            categories = categorizeSkills(skills);
                        }
                        const showCategorized = categories.length > 1;
                        const sectionTitle = categories.length === 1 ? categories[0].label : 'Technical Skills';
                        return (
                            <section key="skills" className="mb-3 font-sans">
                                <SectionHeader title={sectionTitle} />
                                {editMode ? (
                                    <div className="flex flex-wrap gap-1">
                                        {skills.map((skill, i) => (
                                            <span key={i} className="flex items-center gap-0.5 group">
                                                <EditableField
                                                    editMode={editMode}
                                                    value={skill}
                                                    onSave={(v) => onFieldUpdate(`skills.${i}`, v)}
                                                    tag="span"
                                                    className="text-[10px] text-gray-700"
                                                />
                                                {onDeleteSkill && (
                                                    <button
                                                        onClick={() => onDeleteSkill(i)}
                                                        title="Remove skill"
                                                        style={{
                                                            fontSize: 9, lineHeight: 1, color: '#ef4444',
                                                            background: 'rgba(239,68,68,0.08)', border: 'none',
                                                            borderRadius: 3, padding: '1px 3px', cursor: 'pointer',
                                                            opacity: 0, transition: 'opacity 0.15s',
                                                        }}
                                                        className="group-hover:opacity-100"
                                                    >×</button>
                                                )}
                                                {i < skills.length - 1 && <span className="text-gray-400 text-[9.5px] mx-0.5">·</span>}
                                            </span>
                                        ))}
                                    </div>
                                ) : showCategorized ? (
                                    <table className="w-full border-collapse font-sans text-[10px] text-gray-700">
                                        <tbody>
                                            {categories.map((cat, i) => (
                                                <tr key={i}>
                                                    <td className="font-bold text-gray-900 pr-2 pb-0.5 whitespace-nowrap align-top w-[25%]">
                                                        {cat.label}:
                                                    </td>
                                                    <td className="pb-0.5 align-top">
                                                        {cat.items.join(', ')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="text-[10px] text-gray-700">{sortSkills(skills).join(', ')}</p>
                                )}
                            </section>
                        );
                    })() : null,

                    summary: () => summaryText ? (
                        <section key="summary" className="mb-3 font-sans">
                            <SectionHeader title="Professional Summary" />
                            <EditableField
                                editMode={editMode}
                                value={summaryText}
                                onSave={(v) => onFieldUpdate("summary", v)}
                                tag="p"
                                className="text-[10px] text-gray-700 leading-relaxed block text-justify"
                                multiline
                                placeholder="Core technical profile summary..."
                            />
                        </section>
                    ) : null,

                    experience: () => (experience && experience.length > 0) ? (
                        <section key="experience" className="mb-3 font-sans">
                            <SectionHeader title="Work Experience" />
                            <div className="space-y-2.5">
                                {experience.map((exp, index) => (
                                    <div key={index}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <EditableField editMode={editMode} value={exp.role || ""} onSave={(v) => onFieldUpdate(`experience.${index}.role`, v)} tag="h3" className="text-[10.5px] font-bold text-gray-900 leading-snug inline-block" />
                                                <span className="text-gray-400 mx-1.5">|</span>
                                                <EditableField editMode={editMode} value={exp.company || ""} onSave={(v) => onFieldUpdate(`experience.${index}.company`, v)} tag="span" className="text-[10px] text-gray-500 font-medium inline-block" />
                                            </div>
                                            <EditableField editMode={editMode} value={exp.duration || ""} onSave={(v) => onFieldUpdate(`experience.${index}.duration`, v)} tag="span" className="text-[9.5px] text-gray-500 font-medium whitespace-nowrap shrink-0 italic inline-block" />
                                        </div>
                                        {exp.points && exp.points.length > 0 && (
                                            <ul className="mt-1 space-y-0.5 ml-3 list-none">
                                                {exp.points.map((point, i) => {
                                                    const sp = typeof point === "string" ? point : point.description || point.achievements || JSON.stringify(point);
                                                    return (
                                                        <li key={i} className="text-[10px] text-gray-700 leading-relaxed pl-3 relative group/bullet">
                                                            <span className="absolute left-0 text-[10px]" style={{ color: accentColor }}>▪</span>
                                                            <EditableField editMode={editMode} value={sp} onSave={(v) => onFieldUpdate(`experience.${index}.points.${i}`, v)} tag="span" className="pl-0.5 inline-block" multiline />
                                                            {editMode && onDeleteBullet && (
                                                                <button onClick={() => onDeleteBullet('experience', index, i)} title="Delete bullet"
                                                                    style={{ position: 'absolute', right: -20, top: 0, fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 3, padding: '0 4px', cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', lineHeight: '16px' }}
                                                                    className="group-hover/bullet:opacity-100">×</button>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null,

                    internships: () => (internships.length > 0) ? (
                        <section key="internships" className="mb-3 font-sans">
                            <SectionHeader title="Internships" />
                            <div className="space-y-2.5">
                                {internships.map((exp, index) => (
                                    <div key={index}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <EditableField editMode={editMode} value={exp.role || ""} onSave={(v) => onFieldUpdate(`internships.${index}.role`, v)} tag="h3" className="text-[10.5px] font-bold text-gray-900 leading-snug inline-block" />
                                                <span className="text-gray-400 mx-1.5">|</span>
                                                <EditableField editMode={editMode} value={exp.company || ""} onSave={(v) => onFieldUpdate(`internships.${index}.company`, v)} tag="span" className="text-[10px] text-gray-500 font-medium inline-block" />
                                            </div>
                                            <EditableField editMode={editMode} value={exp.duration || ""} onSave={(v) => onFieldUpdate(`internships.${index}.duration`, v)} tag="span" className="text-[9.5px] text-gray-500 font-medium whitespace-nowrap shrink-0 italic inline-block" />
                                        </div>
                                        {exp.points && exp.points.length > 0 && (
                                            <ul className="mt-1 space-y-0.5 ml-3 list-none">
                                                {exp.points.map((point, i) => {
                                                    const sp = typeof point === "string" ? point : point.description || point.achievements || JSON.stringify(point);
                                                    return (
                                                        <li key={i} className="text-[10px] text-gray-700 leading-relaxed pl-3 relative group/bullet">
                                                            <span className="absolute left-0 text-[10px]" style={{ color: accentColor }}>▪</span>
                                                            <EditableField editMode={editMode} value={sp} onSave={(v) => onFieldUpdate(`internships.${index}.points.${i}`, v)} tag="span" className="pl-0.5 inline-block" multiline />
                                                            {editMode && onDeleteBullet && (
                                                                <button onClick={() => onDeleteBullet('internships', index, i)} title="Delete bullet"
                                                                    style={{ position: 'absolute', right: -20, top: 0, fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 3, padding: '0 4px', cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', lineHeight: '16px' }}
                                                                    className="group-hover/bullet:opacity-100">×</button>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null,

                    projects: () => (projectList.length > 0) ? (
                        <section key="projects" className="mb-3 font-sans">
                            <SectionHeader title="Technical Projects" />
                            <div className="space-y-2.5">
                                {projectList.map((proj, index) => (
                                    <div key={index}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <span className="text-[10.5px] font-bold text-gray-900">{proj.title || proj.name}</span>
                                                {proj.tech?.length > 0 && (
                                                    <span className="text-[9.5px] italic ml-2" style={{ color: accentColor }}>
                                                        [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                                                    </span>
                                                )}
                                            </div>
                                            {(proj.impact || proj.status) && (
                                                <span className="text-[9px] font-semibold text-green-650 whitespace-nowrap shrink-0 italic">
                                                    {proj.impact || proj.status}
                                                </span>
                                            )}
                                        </div>
                                        {proj.description && (
                                            <p className="text-[9.5px] text-gray-600 leading-relaxed mt-0.5 ml-3">{proj.description}</p>
                                        )}
                                        {proj.points && proj.points.length > 0 && (
                                            <ul className="mt-1 space-y-0.5 ml-3 list-none">
                                                {proj.points.map((point, i) => {
                                                    const sp = typeof point === "string" ? point : point.description || point.achievements || JSON.stringify(point);
                                                    return (
                                                        <li key={i} className="text-[10px] text-gray-700 leading-relaxed pl-3 relative group/bullet">
                                                            <span className="absolute left-0 text-[10px]" style={{ color: accentColor }}>▪</span>
                                                            <EditableField editMode={editMode} value={sp} onSave={(v) => onFieldUpdate(`projects.${index}.points.${i}`, v)} tag="span" className="pl-0.5 inline-block" multiline />
                                                            {editMode && onDeleteBullet && (
                                                                <button onClick={() => onDeleteBullet('projects', index, i)} title="Delete bullet"
                                                                    style={{ position: 'absolute', right: -20, top: 0, fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 3, padding: '0 4px', cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', lineHeight: '16px' }}
                                                                    className="group-hover/bullet:opacity-100">×</button>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null,

                    education: () => (educationList.length > 0) ? (
                        <section key="education" className="mb-3 font-sans">
                            <SectionHeader title="Education" />
                            <div className="space-y-2">
                                {educationList.map((edu, index) => (
                                    <div key={index} className="flex items-start justify-between gap-4">
                                        <div>
                                            <EditableField editMode={editMode} value={edu?.degree || ""} onSave={(v) => onFieldUpdate(`education.${index}.degree`, v)} tag="h3" className="text-[10.5px] font-bold text-gray-900 inline-block" />
                                            <span className="text-gray-400 mx-1.5">|</span>
                                            <EditableField editMode={editMode} value={edu?.college || ""} onSave={(v) => onFieldUpdate(`education.${index}.college`, v)} tag="span" className="text-[10px] text-gray-500 font-medium inline-block" />
                                            {edu?.cgpa && <span className="text-[9.5px] text-gray-500"> (CGPA: {edu.cgpa})</span>}
                                        </div>
                                        <EditableField editMode={editMode} value={edu?.year || ""} onSave={(v) => onFieldUpdate(`education.${index}.year`, v)} tag="span" className="text-[9.5px] text-gray-500 font-medium whitespace-nowrap shrink-0 italic inline-block" />
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null,

                    certifications: () => (certList.length > 0) ? (
                        <section key="certifications" className="mb-3 font-sans">
                            <SectionHeader title="Certifications" />
                            <ul className="space-y-0.5 list-none">
                                {certList.map((cert, i) => (
                                    <li key={i} className="text-[10px] text-gray-700 pl-3 relative">
                                        <span className="absolute left-0" style={{ color: accentColor }}>▪</span>
                                        <EditableField editMode={editMode} value={cert} onSave={(v) => onFieldUpdate(`certifications.${i}`, v)} tag="span" className="inline-block" />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null,

                    achievements: () => (achieveList.length > 0) ? (
                        <section key="achievements" className="mb-3 font-sans">
                            <SectionHeader title="Achievements & Honors" />
                            <ul className="space-y-0.5 list-none">
                                {achieveList.map((item, i) => (
                                    <li key={i} className="text-[10px] text-gray-700 pl-3 relative">
                                        <span className="absolute left-0" style={{ color: accentColor }}>▪</span>
                                        <EditableField editMode={editMode} value={item} onSave={(v) => onFieldUpdate(`achievements.${i}`, v)} tag="span" className="inline-block" />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null,

                    extracurricular: () => (extraList.length > 0) ? (
                        <section key="extracurricular" className="mb-3 font-sans">
                            <SectionHeader title="Extracurricular Activities" />
                            <ul className="space-y-0.5 list-none">
                                {extraList.map((item, i) => (
                                    <li key={i} className="text-[10px] text-gray-700 pl-3 relative">
                                        <span className="absolute left-0" style={{ color: accentColor }}>▪</span>
                                        <EditableField editMode={editMode} value={item} onSave={(v) => onFieldUpdate(`extracurricular.${i}`, v)} tag="span" className="inline-block" />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null,

                    languages: () => (languageList.length > 0) ? (
                        <section key="languages" className="mb-2 font-sans">
                            <SectionHeader title="Languages" />
                            <p className="text-[10px] text-gray-700">{languageList.join("   ·   ")}</p>
                        </section>
                    ) : null,
                };

                const DEFAULT_ORDER = ['skills', 'summary', 'experience', 'internships', 'projects', 'education', 'certifications', 'achievements', 'extracurricular', 'languages'];
                const savedOrder = Array.isArray(resumeData.section_order) && resumeData.section_order.length > 0
                    ? resumeData.section_order
                    : DEFAULT_ORDER;
                const orderedKeys = [
                    ...savedOrder.filter(k => SECTION_RENDERERS[k]),
                    ...DEFAULT_ORDER.filter(k => SECTION_RENDERERS[k] && !savedOrder.includes(k)),
                ];

                return orderedKeys.map(key => SECTION_RENDERERS[key] ? SECTION_RENDERERS[key]() : null);
            })()}
        </div>
    );
}

export default TechTemplate;
